import { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Button, Input, Textarea, CheckboxGroup, Fieldset } from '@chakra-ui/react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { Controller, useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import api from '@/utils/api';
import { useAuth } from '@/lib/hooks/use-auth';
import { ContentType, SportType, DailogProps } from '@/lib/types';
import { countrySelectItems } from '@/lib/country';

interface Props extends DailogProps {
  onSuccess(): void;
}

interface DecodedToken {
  role_id: number;
}

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }).min(2),
  sportTypeId: z.string().min(1, { message: 'Sport is required' }),
  contentTypes: z.string().array(),
  description: z.string(),
  is_public: z.boolean().optional(),
  country: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function CreateAccountModal({ isOpen, onClose, onSuccess }: Props) {
  const [sportTypes, setSportTypes] = useState<SportType[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const navigate = useNavigate();
  const { changeWorkspace, changeRole } = useAuth();

  const contentRef = useRef<HTMLDivElement>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      sportTypeId: '',
      contentTypes: [],
      description: '',
      country: '',
    },
  });

  const type = useController({
    control,
    name: 'contentTypes',
    defaultValue: [],
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const res = await api.post(`users/new-workspace`, {
        name: data.name,
        sport_type_id: parseInt(data.sportTypeId, 10),
        description: data.description,
        is_public: data.is_public,
        country: data.country,
      });
      const resData = res.data.resource;
      if (resData.id && data.contentTypes.length > 0) {
        const uniqueTypes = [...new Set(data.contentTypes)].map(Number);
        await api.post(`workspace-content-types/multiple`, {
          workspace_id: resData.id,
          content_type_ids: uniqueTypes,
        });
      }
      const resToken = await api.get(`workspace/${resData.id}/token`);
      const tokenData = resToken.data.resource;
      if (tokenData) {
        const decoded = jwtDecode<DecodedToken>(tokenData);
        localStorage.setItem('accessToken', tokenData);
        changeWorkspace(resData);
        changeRole({
          id: decoded?.role_id,
          name: '',
        });
        navigate(`/${resData.id}/home`);
      }
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create workspace',
      });
    }
  };

  const sportItems = useMemo(
    () => sportTypes.map((s) => ({ label: s.name, value: String(s.id) })),
    [sportTypes],
  );

  const getContentTypes = async (sportTypeId: number | null) => {
    if (!sportTypeId) {
      return;
    }
    const response = await api.get(`/content-types/base/${sportTypeId}`);
    setContentTypes(response.data.data as ContentType[]);
  };

  useEffect(() => {
    async function fetchSportTypes() {
      const response = await api.get(`/public/sport-types`);
      const sportTypes = response.data.resource as SportType[];
      const sortedSportTypes = [...sportTypes].sort((a: SportType, b: SportType) =>
        a.name.localeCompare(b.name),
      );
      setSportTypes(sortedSportTypes);
    }
    if (isOpen) {
      fetchSportTypes();
      reset();
    }
  }, [isOpen, reset]);

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent ref={contentRef}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Create new workspace</DialogTitle>
        </DialogHeader>
        <Fieldset.Root>
          <DialogBody>
            <form id={'createWorkspace'} onSubmit={handleSubmit(onSubmit)}>
              <Fieldset.Content>
                <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...register('name')} />
                </Field>
                <Field
                  label='Choose your sport'
                  invalid={!!errors.sportTypeId}
                  errorText={errors.sportTypeId?.message}
                >
                  <SimpleSelect
                    items={sportItems}
                    value={watch('sportTypeId')}
                    onValueChange={(v) => {
                      setValue('sportTypeId', v, { shouldValidate: true });
                      getContentTypes(+v);
                    }}
                    placeholder='Select sport'
                    contentRef={contentRef}
                  />
                </Field>
                {contentTypes.length > 0 && (
                  <Fieldset.Root invalid={!!errors.contentTypes}>
                    <Fieldset.Legend>Content Libraries</Fieldset.Legend>
                    <CheckboxGroup
                      invalid={!!errors.contentTypes}
                      value={type.field.value}
                      onValueChange={type.field.onChange}
                      name={type.field.name}
                      colorPalette='brand'
                    >
                      <Stack gap={3} pl={4}>
                        {contentTypes.map((contentType) => (
                          <Checkbox key={contentType.id} value={contentType.id + ''}>
                            {contentType.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                    {errors.contentTypes && (
                      <Fieldset.ErrorText>{errors.contentTypes.message}</Fieldset.ErrorText>
                    )}
                  </Fieldset.Root>
                )}
                <Field label='Description'>
                  <Textarea rows={6} {...register('description')} />
                </Field>
                <Controller
                  control={control}
                  name='is_public'
                  render={({ field }) => (
                    <Field>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={({ checked }) => field.onChange(checked)}
                      >
                        Make public
                      </Checkbox>
                    </Field>
                  )}
                />
                <Field label='Country'>
                  <SimpleSelect
                    items={countrySelectItems}
                    value={watch('country')}
                    onValueChange={(v) => setValue('country', v)}
                    placeholder='Select country'
                    contentRef={contentRef}
                  />
                </Field>
              </Fieldset.Content>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button colorPalette='brand' variant={'ghost'} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette='brand'
              form={'createWorkspace'}
              type={'submit'}
              loading={isSubmitting}
              loadingText='Loading...'
            >
              Create
            </Button>
          </DialogFooter>
        </Fieldset.Root>
      </DialogContent>
    </DialogRoot>
  );
}
